import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedIcon } from '@/assets/icons/info-icons/rejected';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { AiAssistantIcon } from '@/assets/static/icons/ai-assistant';
import { RevertIcon } from '@/assets/static/icons/revert-icon';
import { Banner } from '@/components/basicComponents/banner';
import Button from '@/components/basicComponents/button';
import { RERUN_AI_SUGGESTION_MUTATION } from '@/components/dueDiligence-page/queries';
import { useResponsive } from '@/hooks/use-responsive';
import { IFieldEditor } from '@/shared/types/fieldEditor';
import { PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import React, { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { useFieldEditorContext } from '../context/FieldEditorContext';

interface FieldEditorContentWrapperProps {
  children: React.ReactNode;
  isEditMode: boolean;
  showRevertPriorDraft?: boolean;
  error?: {
    title: string;
    message: string;
  };
  confirmButton: {
    name: string;
    onClick: () => void;
    disabled?: boolean;
  };
  startWithAISuggestion: () => void;
  editSuggested: (suggestion: string) => void;
  clearSuggestion?: () => void;
  revertPriorDraft?: () => void;
}

const FieldEditorContentWrapper = ({
  children,
  confirmButton,
  error,
  isEditMode,
  showRevertPriorDraft,
  startWithAISuggestion,
  editSuggested,
  clearSuggestion,
  revertPriorDraft
}: FieldEditorContentWrapperProps) => {
  const { isMobile } = useResponsive();
  const theme = useTheme();
  const { currentField, mainQueryRefetch, handleClose } = useFieldEditorContext();

  const [rerunAISuggestionLoading, setRerunAISuggestionLoading] = useState(false);

  const [rerunAISuggestion] = useMutation(RERUN_AI_SUGGESTION_MUTATION);

  const handleRerunAISuggestion = async (currentField: IFieldEditor) => {
    try {
      setRerunAISuggestionLoading(true);

      await rerunAISuggestion({
        variables: {
          data: {
            fieldAnswerId: currentField.fieldAnswerId,
            fieldId: currentField.fieldId,
            promptGroupId: currentField.promptGroup.id,
            recordId: currentField.recordId
          }
        }
      });

      await mainQueryRefetch();
    } catch (error) {
      console.error(error);
    } finally {
      setRerunAISuggestionLoading(false);
    }
  };

  if (!currentField) return <></>;

  return (
    <>
      {error && (
        <Banner
          icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
          title={error.title}
          description={error.message}
          bgColor={themeVar('context.error')}
        />
      )}
      <PaddingWrap>
        <ContentWrapper isMobile={isMobile}>
          {Boolean(currentField.AI?.answers?.length > 0) ? (
            <>
              {isEditMode ? (
                <ControlButtonsWrap isMobile={isMobile}>
                  <Button size="sm" onClick={startWithAISuggestion}>
                    <AiAssistantIcon width={16} height={16} fill={theme.header.action} />
                    Start Over With AI Suggestion
                  </Button>
                  {showRevertPriorDraft && (
                    <Button size="sm" onClick={revertPriorDraft}>
                      <RevertIcon fill={theme.context.light} />
                      Revert to Prior Draft
                    </Button>
                  )}
                  {clearSuggestion && (
                    <SuggestionActionWrap isMobile={isMobile}>
                      <Button size="sm" onClick={clearSuggestion}>
                        <RejectedIcon width={16} height={16} fill={theme.context.light} />
                        Clear
                      </Button>
                    </SuggestionActionWrap>
                  )}
                </ControlButtonsWrap>
              ) : (
                <ControlButtonsWrap isMobile={isMobile}>
                  {showRevertPriorDraft && (
                    <Button size="sm" onClick={revertPriorDraft}>
                      <RevertIcon fill={theme.context.light} />
                      Revert to Prior Draft
                    </Button>
                  )}
                  <SuggestionActionWrap isMobile={isMobile}>
                    <Button size="sm" onClick={() => editSuggested(currentField.AI?.answers[0])}>
                      <ApprovedIcon width={16} height={16} fill={theme.context.light} />
                      Adopt and Edit Suggested
                    </Button>
                    {clearSuggestion && (
                      <Button size="sm" onClick={clearSuggestion}>
                        <RejectedIcon width={16} height={16} fill={theme.context.light} />
                        Clear
                      </Button>
                    )}
                    <Button
                      size="sm"
                      onClick={() => handleRerunAISuggestion(currentField)}
                      styleType={rerunAISuggestionLoading ? 'disabled' : 'default'}
                      isLoading={rerunAISuggestionLoading}
                    >
                      Rerun AI
                    </Button>
                  </SuggestionActionWrap>
                </ControlButtonsWrap>
              )}
            </>
          ) : (
            <ControlButtonsWrap isMobile={isMobile}>
              {clearSuggestion && (
                <SuggestionActionWrap isMobile={isMobile}>
                  <Button size="sm" onClick={clearSuggestion}>
                    <RejectedIcon width={16} height={16} fill={theme.context.light} />
                    Reset
                  </Button>
                </SuggestionActionWrap>
              )}
            </ControlButtonsWrap>
          )}
          <EditPageContent>{children}</EditPageContent>
          <ControlWrapper isMobile={isMobile}>
            <ButtonWrapper>
              <Button size="lg" styleType="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={confirmButton.onClick} styleType={confirmButton.disabled ? 'disabled' : 'default'} size="lg">
                {confirmButton.name}
              </Button>
            </ButtonWrapper>
          </ControlWrapper>
        </ContentWrapper>
      </PaddingWrap>
    </>
  );
};

export default FieldEditorContentWrapper;

const ContentWrapper = styled.div<{ isMobile: boolean }>`
  margin-top: 50px;
  padding: ${({ isMobile }) => (isMobile ? '20px 20px 0' : '60px 60px 0')};
  border: 1px solid ${({ theme }) => theme.border.base};
  border-radius: 10px;
`;

const EditPageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ControlButtonsWrap = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 10px;
  margin-top: ${({ isMobile }) => (isMobile ? '-10px' : '-50px')};
  padding: 20px 0;
`;

const SuggestionActionWrap = styled.div<{ isMobile: boolean }>`
  display: flex;
  align-items: center;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: 10px;
  margin-left: auto;
`;

const ControlWrapper = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '10px' : '0')};
  margin: 20px 0;
`;

const ButtonWrapper = styled.div`
  display: flex;
  gap: 10px;
`;
